import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearLocalData, LOGIN_PATH } from '../lib/auth';
import { navigate } from '../lib/router';
import { ApiError, AuthRequiredError, apiFetch } from './client';

/** Single-user sign-in: /auth/me, /auth/login, /auth/logout, /auth/password and the sessions list. */

export interface Me {
  username: string;
}

export interface SessionView {
  current: boolean;
  device: string;
  remember: boolean;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
}

export const ME_KEY = ['auth', 'me'] as const;
export const SESSIONS_KEY = ['auth', 'sessions'] as const;

/** `null` = signed out (a 401 is the answer here, not an error). An error = the API could not be reached. */
export function useMe() {
  return useQuery<Me | null>({
    queryKey: ME_KEY,
    queryFn: async ({ signal }) => {
      try {
        return await apiFetch<Me>('/auth/me', { signal, allowUnauthorized: true });
      } catch (e) {
        if (e instanceof AuthRequiredError) return null;
        throw e;
      }
    },
    staleTime: Number.POSITIVE_INFINITY,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
}

export type LoginFailure =
  | { kind: 'invalid' }
  | { kind: 'locked'; retryAfterSec: number }
  | { kind: 'other'; error: Error };

export function loginFailure(err: unknown): LoginFailure {
  if (err instanceof AuthRequiredError) return { kind: 'invalid' };
  if (err instanceof ApiError && err.status === 429 && err.code === 'locked') {
    const b = err.body as { retry_after_sec?: unknown };
    return { kind: 'locked', retryAfterSec: typeof b.retry_after_sec === 'number' ? b.retry_after_sec : 900 };
  }
  return { kind: 'other', error: err instanceof Error ? err : new Error(String(err)) };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { username: string; password: string; remember: boolean }) =>
      apiFetch<Me>('/auth/login', { method: 'POST', body, allowUnauthorized: true }),
    onSuccess: (me) => qc.setQueryData(ME_KEY, me),
  });
}

export function useSessions() {
  return useQuery<{ sessions: SessionView[] }>({
    queryKey: SESSIONS_KEY,
    queryFn: ({ signal }) => apiFetch('/auth/sessions', { signal }),
    staleTime: 0,
  });
}

export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { current: string; next: string; signOutOthers: boolean }) =>
      apiFetch<{ ok: true }>('/auth/password', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });
}

export function useRevokeOthers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true; removed: number }>('/auth/sessions/revoke-others', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SESSIONS_KEY }),
  });
}

/** Ends the session on the server; the caller clears local data either way. */
export async function signOutRequest(): Promise<void> {
  try {
    await apiFetch('/auth/logout', { method: 'POST', allowUnauthorized: true });
  } catch {
    // Already signed out, or the API is down: the cookie dies with the server session or its expiry.
  }
}

/** Sign out: end the server session, drop every cached response (memory and localStorage), go to /login. */
export function useSignOut(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await signOutRequest();
    // Leave the signed-in screens first, so nothing on them refetches while the cache is emptied.
    navigate(LOGIN_PATH, { replace: true });
    clearLocalData();
    qc.clear();
  };
}
